import React, { useRef, useState } from 'react';
import { CheckCircle2, Loader2, TriangleAlert, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { errMsg } from '@/services/staffAttendanceApi';
import { directShipApi, ewbDocsApi } from '@/services/ewayBillApi';
import { money } from '@/components/ewayBill/shared';
import { AutoPushSummary } from '@/components/ewayBill/AutoPushSummary';

const invoiceRows = (r) => [
  ...r.invoices.map((i) => ({ key: i.invoice_no, ok: i.matched, text: `${i.invoice_no} · DC ${i.dc_number}`, note: i.matched ? `pick slip ${i.pick_slip_number}` : 'no pick slip with this DC yet' })),
  ...r.errors.map((e, n) => ({ key: `err-${n}`, ok: false, text: e.invoice_no || `pages ${(e.pages || []).join(', ')}`, note: e.reason })),
];
const transferRows = (r) => r.docs.map((d) => ({
  key: d.doc_no, ok: !d.skipped && !d.problems.length, text: `${d.doc_no} · ${d.party || '—'}`,
  note: d.skipped || [`${d.lines} lines · ${d.qty} pcs · ${money(d.taxable)}`, d.moved && 'moved here from Transfer Out (EBO)', ...d.problems].filter(Boolean).join(' · '),
}));
const outsideRows = (r) => r.docs.map((d) => ({
  key: d.doc_no, ok: !d.skipped && !d.problems.length, text: `${d.doc_no} · ${d.party || '—'}${d.code ? ` [${d.code}]` : ''}`,
  note: d.skipped || [`${d.lines} HSN line(s) · ${d.qty} pcs · ${money(d.taxable)}`, d.transporter && `Transporter ${d.transporter}`, ...d.problems].filter(Boolean).join(' · '),
}));
const KINDS = {
  customer_invoice: { label: 'Upload invoice PDF', call: ewbDocsApi.uploadInvoices, rows: invoiceRows, done: (r) => `${r.stored} invoice(s) read from ${r.file_name}` },
  outside_invoice: { label: 'Upload invoice PDF (not in WMS)', call: ewbDocsApi.uploadOutsideInvoices, rows: outsideRows, done: (r) => `${r.docs.length} invoice(s) read from ${r.file_name}` },
  transfer_out: { label: 'Upload Transfer Out PDF', call: ewbDocsApi.uploadTransferOut, rows: transferRows, done: (r) => `${r.docs.length} Transfer Out(s) read from ${r.file_name}` },
  direct_ship: { label: 'Upload Direct Ship Transfer Out', call: directShipApi.upload, rows: transferRows, done: (r) => `${r.docs.length} Transfer Out(s) read — now tick their boxes` },
};

/** Upload a bulk Sales Invoice PDF (each invoice is auto-pushed to Tally) or a Transfer Out PDF; shows what was read from it. */
export const DocUploadButton = ({ kind, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null);
  const [runId, setRunId] = useState(null);
  const k = KINDS[kind];
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    setRows(null);
    setRunId(null);
    k.call(f).then((r) => { setRows(k.rows(r)); setRunId(r.auto_push?.id || null); toast.success(k.done(r)); onDone(); })
      .catch((err) => toast.error(errMsg(err, 'Upload failed'), { duration: 8000 })).finally(() => setBusy(false));
  };
  return (
    <div className="space-y-2">
      <input ref={ref} type="file" accept="application/pdf,.pdf" className="hidden" onChange={pick} data-testid={`ewb-upload-${kind}-input`} />
      <Button size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={busy} data-testid={`ewb-upload-${kind}-btn`}>
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}{busy ? 'Reading PDF…' : k.label}
      </Button>
      {rows && (
        <ul className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-white divide-y text-xs" data-testid="ewb-upload-result">
          {rows.map((r) => (
            <li key={r.key} className="flex items-start gap-2 px-3 py-1.5" data-testid={`ewb-upload-row-${r.key}`}>
              {r.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" /> : <TriangleAlert className="w-4 h-4 shrink-0 text-amber-600" />}
              <span className="font-mono font-semibold">{r.text}</span><span className="text-gray-600">{r.note}</span>
            </li>
          ))}
        </ul>
      )}
      <AutoPushSummary runId={runId} onDone={() => onDone()} />
    </div>
  );
};

export default DocUploadButton;
